import { useState } from "react";

export interface ToggleProps {
  /** Controlled state. Omit to use uncontrolled mode with `defaultChecked`. */
  checked?: boolean;
  /** Initial state when uncontrolled. */
  defaultChecked?: boolean;
  onChange?: (next: boolean) => void;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  /** Accessible name for the toggle (used as aria-label since there's no visible <label>). */
  ariaLabel?: string;
  className?: string;
}

// Offset = track_width - 2*padding - knob_width.
// Track padding is `p-[2px]` (2px each side), so the knob's max translation
// is exactly the inner width minus the knob width. Off-by-one here causes
// the knob to overflow the pill's right edge when active.
const sizeSpec: Record<
  NonNullable<ToggleProps["size"]>,
  { track: string; knob: string; offset: string }
> = {
  sm: {
    track: "h-4 w-7",
    knob: "h-3 w-3",
    // 28 - 4 - 12 = 12
    offset: "translate-x-3 rtl:-translate-x-3",
  },
  md: {
    track: "h-5 w-9",
    knob: "h-4 w-4",
    // 36 - 4 - 16 = 16
    offset: "translate-x-4 rtl:-translate-x-4",
  },
  lg: {
    track: "h-6 w-11",
    knob: "h-5 w-5",
    // 44 - 4 - 20 = 20
    offset: "translate-x-5 rtl:-translate-x-5",
  },
};

/**
 * Pill-style toggle switch. Accent track when on, gray when off. Use the canonical
 * `<Toggle>` instead of bespoke switches for visual consistency. Supports
 * controlled (`checked` + `onChange`) and uncontrolled (`defaultChecked`)
 * modes.
 */
export function Toggle({
  checked,
  defaultChecked = false,
  onChange,
  disabled = false,
  size = "md",
  ariaLabel,
  className = "",
}: ToggleProps) {
  const [internal, setInternal] = useState(defaultChecked);
  const isControlled = checked !== undefined;
  const value = isControlled ? Boolean(checked) : internal;
  const spec = sizeSpec[size];

  const toggle = () => {
    if (disabled) return;
    const next = !value;
    if (!isControlled) setInternal(next);
    onChange?.(next);
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      aria-label={ariaLabel}
      onClick={toggle}
      disabled={disabled}
      className={[
        "relative inline-flex shrink-0 cursor-pointer items-center rounded-full p-[2px] transition-colors",
        spec.track,
        value
          ? "bg-[color:var(--accent-500)]"
          : "bg-[color:var(--border-strong)]",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--accent-500)]",
        disabled ? "cursor-not-allowed opacity-60" : "",
        className,
      ].join(" ")}
    >
      <span
        aria-hidden="true"
        className={[
          "inline-block rounded-full shadow-[0_1px_2px_rgba(0,0,0,0.2)] transition-transform",
          // The accent is monochrome and INVERTS by theme (near-black in light,
          // near-white in dark). A fixed white knob vanishes on the light track
          // in dark mode, so the ON knob uses --on-accent (the color meant to sit
          // on the accent); OFF sits on the dark border track, where white reads.
          value
            ? "bg-[color:var(--on-accent)]"
            : "bg-white border border-[color:var(--border-strong)]",
          spec.knob,
          value ? spec.offset : "translate-x-0",
        ].join(" ")}
      />
    </button>
  );
}
